/**
 * 本人確認の窓（#189、docs/76 ⑤）。アプリの一番外に1つだけ置く（app.tsx）。
 * api.ts が 403 REAUTH_REQUIRED（振込口座・ログイン方法・パスワードの設定・この会社を削除・退会）を受けるとここを開き、
 * 通ったら同じ操作を送り直す（確認コードの窓と同じ作り）。確認後10分は API が聞き直さない。
 *
 *   ・パスワードのある人：「この操作にはパスワードの入力が必要です。」＋パスワード（「表示」）＋「取消」「決定」
 *   ・パスワードの無い人：「この操作には Google／Apple での再認証が必要です。」＋持っている方法のボタン（Google／Apple／パスキー）
 *     Google・Apple は小さな別の窓（ポップアップ）で確かめる（書きかけの欄を消さない）。戻った窓（/oauth/reauth）が
 *     BroadcastChannel で知らせる＋2秒ごとに状態を見る。窓が開けなければ同じ窓で移って戻る（戻ったら同じ操作をもう一度押す）
 */
import { OAUTH_MSG, REAUTH_MSG, type OAuthProvider, type ReauthStatus } from "@genbaryoku/contract";
import { startAuthentication, type PublicKeyCredentialRequestOptionsJSON } from "@simplewebauthn/browser";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { ApiError, api, messageOf, setReauthPrompt } from "../api.js";
import { useMe } from "../auth.js";
import { PASSIVE } from "../lib/autoLogout.js";
import { OAuthLogo, oauthStartUrl } from "./OAuthButtons.jsx";
import { PasskeyIcon, isPasskeyCancel, passkeyErrorText } from "./Passkey.jsx";
import { ErrorText, Field, PasswordInput } from "./ui.jsx";

/** 戻った窓と知らせ合う名前（/oauth/reauth の画面と同じ） */
export const REAUTH_CHANNEL = "gb-reauth";
export type ReauthNotice = { ok: boolean; error?: string | undefined };

type Open = { resolve: () => void; reject: (e: unknown) => void };

export function ReauthDialogHost() {
  const [open, setOpen] = useState<Open | null>(null);
  // 同時に2つの操作が断られても窓は1つ（同じ確かめを待たせる）
  const pending = useRef<Promise<void> | null>(null);
  useEffect(() => {
    setReauthPrompt(() => {
      pending.current ??= new Promise<void>((resolve, reject) =>
        setOpen({
          resolve: () => {
            pending.current = null;
            resolve();
          },
          reject: (e) => {
            pending.current = null;
            reject(e);
          },
        }),
      );
      return pending.current;
    });
    return () => setReauthPrompt(null);
  }, []);
  if (!open) return null;
  return <ReauthDialog {...open} onClose={() => setOpen(null)} />;
}

const cancelled = () => new ApiError(400, { type: "about:blank", status: 400, title: REAUTH_MSG.cancelled, code: "REAUTH_CANCELLED" });

function ReauthDialog({ resolve, reject, onClose }: Open & { onClose: () => void }) {
  const me = useMe();
  const [status, setStatus] = useState<ReauthStatus | null>(null);
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const done = useRef(false);
  const input = useRef<HTMLDivElement>(null);

  const finish = (ok: boolean, e?: unknown) => {
    if (done.current) return;
    done.current = true;
    onClose();
    if (ok) resolve();
    else reject(e ?? cancelled());
  };
  const cancel = () => finish(false);

  // どの方法で確かめるか（ほかのタブで済ませていれば、そのまま通す）
  useEffect(() => {
    let alive = true;
    void api<ReauthStatus>("GET", "/auth/reauth-status", undefined, PASSIVE)
      .then((s) => {
        if (!alive) return;
        if (s.fresh) finish(true);
        else setStatus(s);
      })
      .catch((e: unknown) => alive && finish(false, e));
    return () => {
      alive = false;
    };
    // 開いたときに1回だけ
  }, []);
  useEffect(() => {
    if (status?.methods.password) input.current?.querySelector("input")?.focus();
  }, [status]);

  // Google・Apple の別の窓の終わり：知らせ（BroadcastChannel）か、2秒ごとの状態で
  useEffect(() => {
    if (!waiting) return;
    let alive = true;
    const ch = typeof BroadcastChannel === "function" ? new BroadcastChannel(REAUTH_CHANNEL) : null;
    if (ch) {
      ch.onmessage = (e: MessageEvent<ReauthNotice>) => {
        if (!alive) return;
        if (e.data?.ok) finish(true);
        else {
          setWaiting(false);
          setErr(e.data?.error ?? REAUTH_MSG.failed);
        }
      };
    }
    const t = window.setInterval(() => {
      void api<ReauthStatus>("GET", "/auth/reauth-status", undefined, PASSIVE)
        .then((s) => alive && s.fresh && finish(true))
        .catch(() => undefined);
    }, 2000);
    return () => {
      alive = false;
      ch?.close();
      window.clearInterval(t);
    };
  }, [waiting]);

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (busy) return;
    if (!password) {
      setErr(REAUTH_MSG.passwordRequired);
      return;
    }
    setBusy(true);
    setErr(undefined);
    try {
      await api<ReauthStatus>("POST", "/auth/reauth", { password });
      finish(true);
    } catch (x) {
      setBusy(false);
      setErr(x instanceof ApiError ? messageOf(x) : REAUTH_MSG.failed);
      input.current?.querySelector("input")?.select();
    }
  };

  const withPasskey = async () => {
    setBusy(true);
    setErr(undefined);
    try {
      const optionsJSON = await api<PublicKeyCredentialRequestOptionsJSON>("POST", "/auth/reauth/passkey/options");
      const response = await startAuthentication({ optionsJSON });
      await api<ReauthStatus>("POST", "/auth/reauth/passkey", { response });
      finish(true);
    } catch (x) {
      setBusy(false);
      if (!isPasskeyCancel(x)) setErr(passkeyErrorText(x));
    }
  };

  const withOAuth = (p: OAuthProvider) => {
    setErr(undefined);
    const url = oauthStartUrl(p, "reauth");
    const w = window.open(url, REAUTH_CHANNEL, "popup,width=520,height=720");
    if (!w) {
      // 窓が開けない（止めている）：同じ窓で移って戻る（戻ったら同じ操作をもう一度押す）
      const path = window.location.pathname;
      window.location.assign(`${url}&return=${encodeURIComponent(/^\/[A-Za-z0-9/_-]{0,100}$/.test(path) ? path : "/home")}`);
      return;
    }
    setWaiting(true);
  };

  const methods = status?.methods;
  const noPassword = !!methods && !methods.password;
  const providers = (["google", "apple"] as const).filter((p) => methods?.[p]);
  // body の直下に、ほかの窓（会社の削除の確認・パスキーの案内など）より上に描く（その窓の操作から開くため）
  return createPortal(
    <div className="dim reauthdim" data-testid="reauth-dialog">
      <form className="dlg reauthdlg" role="dialog" aria-modal="true" aria-labelledby="gb-reauth-h" onSubmit={(e) => void submit(e)} onKeyDown={(e) => e.key === "Escape" && !busy && cancel()} noValidate>
        <div className="dh" id="gb-reauth-h">
          {REAUTH_MSG.title}
        </div>
        <div className="db">
          {!status ? (
            <p className="hint" style={{ margin: 0 }}>…</p>
          ) : noPassword ? (
            <>
              <p style={{ margin: 0 }} data-testid="reauth-lead">{REAUTH_MSG.via(methods)}</p>
              <div className="reauth-ways">
                {providers.map((p) => (
                  <button key={p} type="button" className={`btn oauthbtn ${p}`} onClick={() => withOAuth(p)} disabled={busy || waiting} data-testid={`reauth-${p}`}>
                    <OAuthLogo provider={p} onDark={p === "apple"} />
                    <span>{OAUTH_MSG.reauthWith(p)}</span>
                  </button>
                ))}
                {methods.passkey ? (
                  <button type="button" className="btn oauthbtn passkey" onClick={() => void withPasskey()} disabled={busy || waiting} data-testid="reauth-passkey">
                    <PasskeyIcon />
                    <span>{REAUTH_MSG.passkey}</span>
                  </button>
                ) : null}
                {!providers.length && !methods.passkey ? <p className="hint">{REAUTH_MSG.noMethod}</p> : null}
              </div>
            </>
          ) : (
            <>
              <p style={{ margin: 0 }} data-testid="reauth-lead">{REAUTH_MSG.password}</p>
              {/* パスワード管理がユーザー名つきで覚える・入れる（CLAUDE.md：パスワードの欄の form には autocomplete="username"） */}
              <input type="email" name="username" autoComplete="username" value={me.data?.user.email ?? ""} readOnly hidden />
              <div ref={input}>
                <Field id="gb-reauth-pw" label={REAUTH_MSG.passwordLabel}>
                  <PasswordInput id="gb-reauth-pw" autoComplete="current-password" value={password} onChange={setPassword} />
                </Field>
              </div>
            </>
          )}
          <ErrorText>{err}</ErrorText>
        </div>
        <div className="df">
          <span className="sp" />
          <button type="button" className="btn text" onClick={cancel} disabled={busy} data-testid="reauth-cancel">
            {REAUTH_MSG.cancel}
          </button>
          {status && !noPassword ? (
            <button type="submit" className="btn primary" disabled={busy} data-testid="reauth-submit">
              {REAUTH_MSG.confirm}
            </button>
          ) : null}
        </div>
      </form>
    </div>,
    document.body,
  );
}

/** /oauth/reauth の画面が使う：別の窓の終わりを元の窓へ知らせる */
export function notifyReauth(n: ReauthNotice): void {
  try {
    const ch = new BroadcastChannel(REAUTH_CHANNEL);
    ch.postMessage(n);
    ch.close();
  } catch {
    // BroadcastChannel の無いブラウザ：元の窓は2秒ごとの状態で気づく
  }
}
